/**
 * Backup export and import.
 *
 * Until sync exists this is the only way work leaves an iPad, so it has to be
 * complete and it has to be honest. Export carries raw strokes and semantic
 * corrections, not a summary. Import validates first, reports what it will add and
 * what it will replace, and never runs anything it read.
 *
 * Imported data is data. Nothing here evaluates a string, builds a function, or
 * trusts a field to be the type it claims.
 */

import type { LessonProgress } from '../curriculum/types';
import { migrateDocument } from './documents';
import type { Repositories } from './repositories';
import type { StoredDocument } from './types';

export const BACKUP_FORMAT = 'musicmentor.backup';
export const BACKUP_VERSION = 1;

export interface Backup {
  format: typeof BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  documents: StoredDocument[];
  lessonProgress: LessonProgress[];
  settings: { key: string; value: Record<string, unknown> }[];
}

export interface ImportPlan {
  /** Not on this iPad yet. */
  add: StoredDocument[];
  /** Here already, but the backup's copy is genuinely newer. */
  replace: StoredDocument[];
  /** Here already and at least as new. Left alone. */
  skip: StoredDocument[];
  lessonProgress: LessonProgress[];
  settings: { key: string; value: Record<string, unknown> }[];
  /** Records that could not be read. Reported, never silently dropped. */
  damaged: number;
}

export async function exportBackup(
  repos: Repositories,
  progress: Record<string, LessonProgress>,
): Promise<Backup> {
  // Deleted documents travel too: a backup that quietly drops them would
  // resurrect them on the next import.
  const documents = await repos.documents.list({ includeDeleted: true });
  const settings = await repos.settings.all();
  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    documents,
    lessonProgress: Object.values(progress),
    settings: settings.map((row) => ({ key: row.key, value: row.value })),
  };
}

export function serializeBackup(backup: Backup): string {
  return JSON.stringify(backup, null, 2);
}

export class BackupError extends Error {}

/** Parse and validate. Throws with a reason a person can act on. */
export function parseBackup(text: string): Backup {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new BackupError('That file is not valid JSON.');
  }
  if (!raw || typeof raw !== 'object') throw new BackupError('That file is not a backup.');

  const candidate = raw as Partial<Backup>;
  if (candidate.format !== BACKUP_FORMAT) {
    throw new BackupError('That file is not a MusicMentor backup.');
  }
  if (typeof candidate.version !== 'number' || candidate.version > BACKUP_VERSION) {
    throw new BackupError(
      `That backup was made by a newer version of MusicMentor (v${String(candidate.version)}).`,
    );
  }
  return {
    format: BACKUP_FORMAT,
    version: candidate.version,
    exportedAt: typeof candidate.exportedAt === 'string' ? candidate.exportedAt : '',
    documents: Array.isArray(candidate.documents) ? candidate.documents : [],
    lessonProgress: Array.isArray(candidate.lessonProgress) ? candidate.lessonProgress : [],
    settings: Array.isArray(candidate.settings) ? candidate.settings : [],
  };
}

/**
 * Work out what an import would do, without doing any of it.
 *
 * A document already on this iPad is replaced only when the backup's copy is
 * genuinely newer. Restoring an old backup should not roll back work done since.
 */
export async function planImport(backup: Backup, repos: Repositories): Promise<ImportPlan> {
  const existing = new Map(
    (await repos.documents.list({ includeDeleted: true })).map((doc) => [doc.id, doc]),
  );

  const add: StoredDocument[] = [];
  const replace: StoredDocument[] = [];
  const skip: StoredDocument[] = [];
  let damaged = 0;

  for (const raw of backup.documents) {
    const document = migrateDocument(raw);
    if (!document) {
      damaged++;
      continue;
    }
    const current = existing.get(document.id);
    if (!current) add.push(document);
    else if (document.updatedAt > current.updatedAt) replace.push(document);
    else skip.push(document);
  }

  return {
    add,
    replace,
    skip,
    damaged,
    lessonProgress: backup.lessonProgress.filter(
      (row): row is LessonProgress =>
        Boolean(row) && typeof (row as LessonProgress).lessonId === 'string',
    ),
    settings: backup.settings.filter(
      (row) => row && typeof row.key === 'string' && typeof row.value === 'object',
    ),
  };
}

export async function applyImport(plan: ImportPlan, repos: Repositories): Promise<void> {
  for (const document of [...plan.add, ...plan.replace]) {
    await repos.documents.put(document);
  }
  for (const row of plan.settings) {
    await repos.settings.set(row.key, row.value);
  }
}

export function describePlan(plan: ImportPlan): string {
  const parts: string[] = [];
  if (plan.add.length) parts.push(`${plan.add.length} new`);
  if (plan.replace.length) parts.push(`${plan.replace.length} replaced`);
  if (plan.skip.length) parts.push(`${plan.skip.length} already newer here`);
  if (plan.lessonProgress.length) parts.push(`${plan.lessonProgress.length} lesson records`);
  if (plan.damaged) parts.push(`${plan.damaged} unreadable`);
  return parts.length ? parts.join(', ') : 'nothing to import';
}
